import {
  useState,
  ChangeEvent,
  useMemo
} from "react";
import SimpleBar from "simplebar-react";
import { Icon } from "@/components/ui/Icon"
import OutsideClickHandler from "../OutsideClickHandler";

import "./countries-dropdown.css"

import { cn } from "@/utils/utils";

import { FlagOption, Flag } from "./Flag";
import unifiedCountries from "./unifiedCountries";
import { useTranslation } from "react-i18next";

const excludedCountries = ["AX", "IO", "GG", "MF", "PM", "VC", "SJ"];

type CountryData = {
  label: string;
  code: string;
};

type CountryCodeSelectProps = {
  defaultCountryCode: string;
  defaultPhoneNumberCode: string;
  isError?: boolean;
  isFocus?: boolean;
  isHover?: boolean;
  onChange: (country: Country) => void;
  disabled?: boolean;
  value?: string;
};

export type Country = {
  code: string;
  phoneNumberCode: string;
};

const ChevronDown = 'StreamlineArrowsArrowDown1Regular';
const ChevronUp = 'StreamlineArrowsArrowUp1Regular';

export function CountryCodeSelect(props: CountryCodeSelectProps) {
  const [openSelect, setOpenSelect] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { t } = useTranslation();

  const countryOptions = useMemo(() => {
    const searchTextLower = searchText.toLowerCase();
    const countries = Object.entries(unifiedCountries)
      .map(([code, phoneNumberCodes]) => {
        const data = phoneNumberCodes as CountryData;
        return {
          label: data.label,
          value: code,
          countryCode: code,
          phoneNumberCode: data.code,
        };
      })
      .filter((o) => o.label)
      .filter((o) => !excludedCountries.includes(o.countryCode))
      .sort((a, b) => a.label.localeCompare(b.label))

    if (searchTextLower) {
      return countries.filter((o) => o.label.toLowerCase().includes(searchTextLower));
    }

    return countries;
  }, [unifiedCountries, searchText])


  const handleSelectOnChange = (countryCode: string) => {
    const { onChange } = props;

    const option = countryOptions.find((o: any) => o.countryCode === countryCode);

    setOpenSelect(false);
    setSearchText("");

    if (option) {
      onChange({
        code: option.countryCode,
        phoneNumberCode: `+${option.phoneNumberCode}`,
      });
    }
  };

  const countryCodeSelected = useMemo(() => {
    const option = countryOptions.find(
      (o: any) =>
        o.phoneNumberCode === props.value?.replace("+", "")
    );
    return option?.countryCode;
  }, [props.value]);

  const handleButtonOnClick = () => {
    setOpenSelect(!openSelect);
    setSearchText("");
  };

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleOutsideClick = () => {
    if (openSelect) {
      setOpenSelect(false);
      setSearchText("");
    }
  };

  const renderDropdownPanel = () => {
    return (
      <div className={cn("country-code-menu",
        "absolute w-100 text-base z-50 pt-6 mt-1",
        "bg-[var(--surface-default)] border border-[var(--outline-hover)] mt-4 rounded-xl"
      )}>
        <div className="country-search py-0 px-4">
          <div className="country-search-input text-center flex flex-row w-full items-center bg-[var(--surface-default)]  border border-[var(--outline-hover)] pl-8 rounded-lg">
            <Icon name="StreamlineEssentialSearchRegular" size="sm"
              className="text-[var(--on-surface-variant-2)]" />
            <input
              type="text"
              placeholder={t("common.searchByCountry")}
              value={searchText || ""}
              className={cn("w-full rounded-lg bg-[var(--surface-default)]  py-4 px-8 border-none text-[var(--on-surface-variant-2)] outline-none",
                // "pt-[var(--spacing-spacing-xs)] pb-[var(--spacing-spacing-xs)] pl-[var(--spacing-spacing-md)] pr-[var(--spacing-spacing-md)]"
              )}
              onChange={handleSearchOnChange}
            />
          </div>
        </div>
        <SimpleBar
          forceVisible="y"
          autoHide={false}
          className="socialLoginConsent_body_text socialLoginConsent"
          scrollbarMaxSize={50}
        >
          {countryOptions.length ? (
            <div className="countries-list">
              {countryOptions.map(({ label, countryCode, phoneNumberCode }) => (
                <FlagOption
                  key={`${countryCode}`}
                  countryCode={countryCode}
                  label={label}
                  selected={countryCode === countryCodeSelected}
                  phoneNumberCode={phoneNumberCode}
                  onClick={handleSelectOnChange}
                />
              ))}
            </div>
          ) : (
            <div className="w-100 text-center text-[var(--on-surface-variant-2)]">{t("common.no_results")}</div>
          )}
        </SimpleBar>
      </div>
    );
  };

  return (
    <OutsideClickHandler outsideclick={handleOutsideClick}>
      <div className="country-code-panel">
        <button
          type="button"
          disabled={props.disabled}
          className={cn(props.isFocus ? "focus" : "",
            "flex items-center w-36 h-24 rounded-lg py-0 px-4",
            "bg-[var(--surface-default)]",
            "text-[var(--on-surface-variant-2)]",
            "border-[1px] border-[var(--outline-default)]",
            "border-l-lg rounded-r-none border-r-[0] ",
            props.disabled ? "bg-[var(--surface-disabled)] border-[var(--outline-disabled)] " : "",
          )}
          onClick={handleButtonOnClick}
        >
          <Flag disabled={props.disabled} countryCode={countryCodeSelected || ""} />
          {!props.disabled && <Icon name={openSelect ? ChevronUp : ChevronDown} size="sm" className="ml-6" />}
        </button>
        {openSelect && renderDropdownPanel()}
      </div>
    </OutsideClickHandler>
  );
} 
